// MACHINERY V2 (Stroje) — Filter pattern B, row 2 (M2.16a).
//
// Spec: mobile/README.md §THE DECISION anatomy point 2 — "dismissible chips
// summarizing each active filter (`Stav: Bezia + Alarm ✕`, h 26, radius 999,
// --card-2 bg) + a 'Zrusit' text button clearing all". Rendered truth:
// mobile/screenshots/01-filter-b-chosen.png. Reference impl:
// mobile/machinery-mobile.jsx MbFilterB (:87-94).
//
// RENDERED ONLY WHEN FILTERS ARE ACTIVE — an empty row would be 44px of blank
// between the search field and the fleet. The caller decides; this component
// renders whatever chip list it is handed.
//
// >>> THE WHOLE CHIP IS THE BUTTON, AND THAT IS THE 44px RULE, NOT A
// PREFERENCE. <<< The law as amended at the M2.16a fix pass is ">= 44px BOTH
// axes; minWidth accompanies height everywhere the law applies", and it
// overrides the board where they disagree — and here they disagree, because the
// board's chip is 26px tall. Resolved WITHOUT changing the drawing: the button
// is 44px tall, 44px wide at minimum, and TRANSPARENT, with the 26px pill
// painted by a span inside it and centred in whatever is left over. The row
// therefore looks exactly like the board and touches like the standard. The
// ✕ is decorative (aria-hidden) — making it a nested button would put a second,
// smaller target inside the first, which is worse on a phone than no ✕ at all.
//
// The "Zrusit" action is the LAST child, and it used to sit under ChipScroller's
// permanent right-edge fade. The scroller now reserves a spacer exactly one
// fade wide after its children, so this action can always be read in full — see
// ChipScroller.tsx's header. Do not add horizontal padding here to work around
// a fade; the fix belongs in the scroller, once, for all four consumers.
//
// The row scrolls through ChipScroller rather than wrapping: two filters fit at
// 390px, four do not, and a wrapping row pushes the fleet down the screen on the
// exact device that has least of it.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import ChipScroller from "./ChipScroller";
import { MbDismissGlyph } from "./mobileGlyphs";

export interface ActiveFilterChip {
  /** Stable key — the filter GROUP it summarises ("status", "location", …). */
  id: string;
  /** Already-composed summary, e.g. "Stav: bezi + alarm". */
  label: string;
  onRemove: () => void;
}

export interface ActiveFilterChipsProps {
  chips: ActiveFilterChip[];
  onClearAll: () => void;
  /** What the ChipScroller's fade resolves to — the page background here. */
  fadeTo?: string;
}

const ActiveFilterChips: React.FC<ActiveFilterChipsProps> = ({
  chips,
  onClearAll,
  fadeTo,
}) => {
  const { translate } = useTranslation("myMachinery");
  if (chips.length === 0) return null;

  return (
    <ChipScroller fadeTo={fadeTo} gap={6} paddingBlock={9}>
      {chips.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={c.onRemove}
          aria-label={translate("myMachinery:v2.mobile.removeFilter", { name: c.label })}
          style={{
            // 44x44 target, transparent — the pill below paints the 26px chip.
            height: 44,
            minWidth: 44,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            border: 0,
            background: "transparent",
            padding: 0,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              height: 26,
              padding: "0 9px",
              borderRadius: 999,
              background: "var(--card-2)",
              border: "1px solid var(--border)",
              fontSize: 11,
              color: "var(--ink-2)",
              whiteSpace: "nowrap",
            }}
          >
            {c.label}
            <span style={{ color: "var(--faint)", display: "inline-flex" }}>
              <MbDismissGlyph />
            </span>
          </span>
        </button>
      ))}

      <button
        type="button"
        onClick={onClearAll}
        style={{
          height: 44,
          minWidth: 44,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          border: 0,
          background: "transparent",
          padding: "0 4px",
          cursor: "pointer",
          fontFamily: "inherit",
          fontSize: 11,
          fontWeight: 600,
          color: "var(--mod-accent)",
          whiteSpace: "nowrap",
        }}
      >
        {translate("myMachinery:v2.mobile.clear")}
      </button>
    </ChipScroller>
  );
};

export default ActiveFilterChips;
